import {
  DecimalPipe,
  TitleCasePipe,
  DatePipe,
  CommonModule,
} from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { AllOrdersDatum } from '@core/models/admin/orders';
import { Pagination } from '@core/models/pagination.model';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { MutualFundService } from '@core/services/mutual-fund.service';
import { OrderService } from '@core/services/order.service';
import { ProductsService } from '@core/services/products.service';
import { Store } from '@ngxs/store';
import { dropdownMenuAnimation } from '@shared/animations/animations';
import { ConfirmDialogComponent } from '@shared/ui/confirm-dialog/confirm-dialog.component';
import {
  DropdownComponent,
  DropDownModel,
} from '@shared/ui/dropdown/dropdown.component';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { StatusComponent } from '@shared/ui/status/status.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { IFILTER } from 'app/app.constants';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { Subscription, BehaviorSubject, Observable, take } from 'rxjs';
import { PermissionDirective } from "@shared/directives/permission.directive";
import { PermissionService } from '@core/services/permission.service';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';
@Component({
  selector: 'app-customer-order-table',
  standalone: true,
  imports: [
    TableNavigatorComponent,
    ConfirmDialogComponent,
    StatusComponent,
    FiltersComponent,
    TableSkeletonLoaderComponent,
    DropdownComponent,
    CommonModule,
    PermissionDirective,
    TableRefreshComponent
],
  templateUrl: './customer-order-table.component.html',
  styleUrl: './customer-order-table.component.scss',
  animations: [dropdownMenuAnimation],
  providers: [TitleCasePipe, DecimalPipe, DatePipe],
})
export class CustomerOrderTableComponent implements OnInit, OnDestroy {
  mfService = inject(MutualFundService);
  filterMenuState: 'visible' | 'hidden' = 'hidden';
  subs: Subscription[] = [];
  ordersList: AllOrdersDatum[] = [];
  filteredList: any[] = [];
  actionList: DropDownModel[] = [];
  totalCount: number = 0;
  totalRejected = 0;
  totalInitiated = 0;
  totalSales = 0;
  isFiltered: boolean = false;
  isLoading: boolean = false;
  orderId!: string | null;
  orderType!: string | null;
  approveDialogQuestion!: any;
  rejectDialogQuestion!: any;
  dialogQuestion!: any;
  isReverseDialogOpen: boolean = false;
  isReverseInProgress: boolean = false;
  customerOrdersUrl!: string;
  orderApprovalUrl!: string;

  orderListSubject = new BehaviorSubject<any>(null);
  totalCountSubject = new BehaviorSubject<number>(0);
  orderList$ = this.orderListSubject.asObservable();
  totalCount$: Observable<number> = this.totalCountSubject.asObservable();
  isFetchingOrderList$!: Observable<boolean>;
  hadFilterBefore = false;
  isFilterActive: boolean = false;

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private breadCrumbService = inject(BreadCrumbService);
  private orderService = inject(OrderService);
  private change = inject(ChangeDetectorRef);
  private notify = inject(NotificationFacade);
  private decimalPipe = inject(DecimalPipe);
  private titleCasePipe = inject(TitleCasePipe);
  private datePipe = inject(DatePipe);
  private store = inject(Store);
  private confirmDialogService = inject(ConfirmDialogService);
  private permissionService = inject(PermissionService);
  private productsService = inject(ProductsService);

  filterStructure: IFILTER[] = [
    {
      filterKey: 'date',
      label: 'Date Created',
      type: 'date',
    },
    {
      filterKey: 'type',
      label: 'Order Type',
      type: 'dropdown',
      dropdown: [
        { key: 'invest', value: 'Invest' },
        { key: 'liquidate', value: 'Liquidate' },
        { key: 'partial_redemption', value: 'Partial Redemption' },
        { key: 'total_redemption', value: 'Total Redemption' },
      ],
    },
    {
      filterKey: 'status',
      label: 'Status',
      type: 'dropdown',
      dropdown: [
        { key: 'initiated', value: 'Initiated' },
        { key: 'in_process', value: 'In Process' },
        { key: 'completed', value: 'Completed' },
        { key: 'terminated', value: 'Terminated' },
      ],
    },
    {
      filterKey: 'product_id',
      label: 'Product',
      type: 'dropdown',
      dropdown: [
        
      ]
    },
    {
      filterKey: 'currency',
      label: 'Currency',
      type: 'dropdown',
      dropdown: [
        { key: 'NGN', value: 'NGN' },
        { key: 'USD', value: 'USD' },
      ],
    },
  ];
  constructor() {
    this.orderList$ = this.store.select(TableSelectors.getTableData('orders'));
    this.totalCount$ = this.store.select(
      TableSelectors.getTableTotalCount('orders')
    );
    this.isFetchingOrderList$ = this.store.select(
      TableSelectors.getTableLoading('orders')
    );
  }

  ngOnInit() {
    this.breadCrumbService.setPageBreadCrumb(this.activatedRoute);
    this.filterEventListener();
    this.loadOrdersList();
    this.loadGlobalProducts();
  }

  loadGlobalProducts() {
    const sub = this.productsService.getGlobalProducts().subscribe({
      next: (res) => {
        const productFilter = this.filterStructure.find(
          (f) => f.filterKey === 'product_id'
        );
        if (productFilter && res?.data) {
          productFilter.dropdown = res.data.map((product) => ({
            key: product.id,
            value: product.name,
          }));
          this.change.markForCheck();
        }
      },
      error: () => {
        // silently fail — filter remains empty
      },
    });
    this.subs.push(sub);
  }

  loadOrdersList(page = 1) {
    const sub = this.orderList$.pipe(take(1)).subscribe((order) => {
      const hasData = Array.isArray(order) && order.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'orders',
            params: new Pagination({
              skip: (page - 1) * 10,
              size: 10,
            }),
          })
        );
      }
    });
    this.subs.push(sub);
  }

  filterEventListener() {
    this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';
      if (params[this.filterStructure[1].filterKey]) {
        query = `type=${params['type']}&`;
      }
      if (params[this.filterStructure[2].filterKey]) {
        query += `status=${params['status']}&`;
      }
      if (params[this.filterStructure[3].filterKey]) {
        query += `product_id=${params['product_id']}&`;
      }
      if (params[this.filterStructure[4].filterKey]) {
        query += `currency=${params['currency']}&`;
      }
      if (params['search']) {
        query += `search_text=${params['search']}&`;
      }

      // Handle pagination
      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        query += `skip=${skip}&take=${take}`;
        this.isFilterActive = true;
        this.hadFilterBefore = true;
        this.store.dispatch(
          new TableActions.FilterOrderList({ filter: query })
        );
      } else {
        // only re-fetch if there was a previous filter
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'orders',
              params: new Pagination({ skip: skip, size: take }),
            })
          );
        }
      }
    });
  }

  navigate(currentPage: number) {
    if (this.isFilterActive) {
      this.router.navigate([], {
        queryParams: { page: currentPage },

        queryParamsHandling: 'merge',
      });
    }
  }



  toggle() {
    this.change.detectChanges();
  }


  initDropdownList(order: AllOrdersDatum) {
    return (this.actionList = [
      {
        action: () => {
          this.router.navigateByUrl(`/admin/orders/details/${order.id}`);
        },
        title: 'View Details',
        show: true && this.permissionService.hasPermission(['get_order_by_id']),
      },
      {
        action: () => {
          this.router.navigateByUrl(`/admin/orders/approve/${order.id}`);
        },
        title: 'Approve',
        show: order.status !== 'completed' && this.permissionService.hasPermission(['approve_order']),
      },
      {
        action: () => {
          this.orderId = order.id;
          this.orderType = order.type;
          this.dialogQuestion = this.setDialogQuestion(order, 'reject');
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Reject Order',
            description: this.dialogQuestion,
            loadingText: 'Rejecting Order, please wait...',
            action: () => {
              this.rejectOrder();
            },
          });
        },
        title: 'Reject',
        show: order.status !== 'terminated' && this.permissionService.hasPermission(['reject_order']),
      },
      {
        action: () => {
          this.orderId = order.id;
          this.orderType = order.type;
          this.isReverseDialogOpen = true;
          this.dialogQuestion = this.setDialogQuestion(order, 'reverse');
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Reverse Order',
            description: this.dialogQuestion,
            loadingText: 'Reversing Order, please wait...',
            action: () => {
              this.reverseOrder();
            },
          });
        },
        title: 'Reverse Order',
        show: order.status == 'completed' &&  this.permissionService.hasPermission(['can_reverse_user_order']),
      },

    ]);
  }


  approveOrder() {
    if (!this.orderType) {
      this.notify.info('Order type is required');
    }
    if (!this.orderId) {
      this.notify.info('Order ID is required');
      return;
    }
    this.confirmDialogService.updateDialog({ isDialogLoading: true });

    if (this.orderType === 'liquidate' || this.orderType === 'invest') {
      const sub = this.store
        .dispatch(new TableActions.ApproveOrder({ orderId: this.orderId }))
        .subscribe({
          complete: () => {
            // This runs when the action completes (success or error)
            this.confirmDialogService.updateDialog({
              isDialogLoading: false,
              isOpen: false,
            });
            this.change.detectChanges();
          },
          error: (err) => {
            this.confirmDialogService.updateDialog({ isDialogLoading: false });
            this.change.detectChanges();
          },
        });
      this.subs.push(sub);
    }
    if (
      this.orderType === 'partial_redemption' ||
      this.orderType === 'total_redemption'
    ) {
      const sub = this.store
        .dispatch(
          new TableActions.ApproveMFTransaction({ orderId: this.orderId })
        )
        .subscribe({
          complete: () => {
            // This runs when the action completes (success or error)
            this.confirmDialogService.updateDialog({
              isDialogLoading: false,
              isOpen: false,
            });
            this.change.detectChanges();
          },
          error: (err) => {
            this.confirmDialogService.updateDialog({ isDialogLoading: false });
            this.change.detectChanges();
          },
        });
      this.subs.push(sub);
    }
  }

  rejectOrder() {
    if (!this.orderType) {
      this.notify.info('Order type is required');
    }
    if (!this.orderId) {
      this.notify.info('Order ID is required');
      return;
    }

    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    if (this.orderType === 'liquidate' || this.orderType === 'invest') {
      const sub = this.store
        .dispatch(new TableActions.RejectOrder({ orderId: this.orderId }))
        .subscribe({
          complete: () => {
            // This runs when the action completes (success or error)
            this.confirmDialogService.updateDialog({
              isDialogLoading: false,
              isOpen: false,
            });
            this.change.detectChanges();
          },
          error: (err) => {
            this.confirmDialogService.updateDialog({ isDialogLoading: false });
            this.change.detectChanges();
          },
        });
      this.subs.push(sub);
    }
    if (
      this.orderType === 'partial_redemption' ||
      this.orderType === 'total_redemption'
    ) {
      const sub = this.store
        .dispatch(
          new TableActions.RejectMFTransaction({ orderId: this.orderId })
        )
        .subscribe({
          complete: () => {
            // This runs when the action completes (success or error)
            this.confirmDialogService.updateDialog({
              isDialogLoading: false,
              isOpen: false,
            });
            this.change.detectChanges();
          },
          error: (err) => {
            this.confirmDialogService.updateDialog({ isDialogLoading: false });
            this.change.detectChanges();
          },
        });
      this.subs.push(sub);
    }
  }
  reverseOrder() {
    if (!this.orderId) {
      this.notify.info('Order ID is required');
      return;
    }

    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    const sub = this.orderService.reverseOrder(this.orderId)
      .subscribe({
        next: (response) => {
          this.confirmDialogService.updateDialog({
            isDialogLoading: false,
            isOpen: false,
          });
          this.notify.success('Order reversed successfully');
          this.store.dispatch(new TableActions.LoadTableData({
            tableKey: 'orders',
            params: new Pagination({ skip: 0, size: 10 }),
          }));
          this.change.detectChanges();
        },
        error: (err) => {
          this.confirmDialogService.updateDialog({ isDialogLoading: false });
          this.notify.error('Failed to reverse order');
          this.change.detectChanges();
        }
      });
    this.subs.push(sub);
  }



  setDialogQuestion(order: AllOrdersDatum, type: 'approve' | 'reject' | 'reverse') {
    const accountName =
      order.account?.account_type === 'corporate'
        ? order.account?.corporate_kyc?.name || '—'
        : `${this.titleCasePipe.transform(order.account.user_profile?.first_name)} ${this.titleCasePipe.transform(order.account.user_profile?.last_name)}`;
    const productName = order?.product ? order?.product?.name : 'not provided';
    let amount = this.decimalPipe.transform(order.amount / 100, '1.2-2');
    let date = this.datePipe.transform(order.created_at, 'medium');
    const currency = order.currency === 'USD' ? '$' : '₦';

    let orderTypeClass =
      order.type === 'invest'
        ? 'bg-green-100 text-mango-pineGreen'
        : 'bg-mango-shellRed text-mango-red707';

    return `
          <p>Are you sure you want to ${type} this Order? This action cannot be undone. </p>

          <div class="flex flex-col text-sm gap-1 mt-1">
            <h5>Recipient Details</h5>
            <div class="flex justify-between">
              <div class="text-gray-500"> User/Organization: </div>
              <div class="font-bold">${accountName}</div>
            </div>
            <div class="flex justify-between">
              <div class="text-gray-500"> Order Type: </div>
              <span
                class="inline-flex w-fit items-center gap-1 rounded-full px-2 py-1 text-xs font-medium uppercase ${orderTypeClass}"
              >
              ${order.type ? order.type : order.type == 'total_redemption' || order.type == 'partial_redemption' ? 'Mutual Funds' : 'not provided'}
              </span>
            </div>
            <div class="flex justify-between">
              <div class="text-gray-500"> Products: </div>
              <div class="font-bold"> ${productName}</div>
            </div>
            <div class="flex justify-between">
              <div class="text-gray-500"> Amount: </div>
              <div class="font-bold">${currency}${amount}</div>
            </div>
            <div class="flex justify-between">
              <div class="text-gray-500"> Date: </div>
              <div class="font-bold">${date}</div>
            </div>
          </div>
          `;
  }

  getProductName(order: AllOrdersDatum) {
    console.log('type', order?.product?.type )
    if (order?.product?.type == "tbill_base"){
      return `${order?.product?.name} ${Number(order?.product?.buy_rate) / 100 }% ${this.datePipe.transform(order?.product?.maturity_date, 'MM/dd/yyyy')}`;
    } else if (order?.product?.type == "rate_base") {
        return `${order?.product?.name} ${order?.rate != null ? order.rate + '% Fixed Deposit' : 'Fixed Deposit'}`;
    }
    return order?.product?.name || '—';
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
